import '@components/Model/model.css'

import { useMetadata } from '@api/useMetadata'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { useInterval } from '@utils/general'
import { Col, Row } from 'antd'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'

import { Configuration } from './Configuration/Configuration'
import { LPSettingsModal } from './Configuration/LPSettingsModal'
import { Grid } from './Grid/Grid'
import { Logs } from './Log/Logs'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useLpConfig } from '@hooks/useLpConfig'
import { useUserSettings } from '@hooks/settings'

export function Optimizer() {
  const queryClient = useQueryClient()
  const [googleSheetKey, setGoogleSheetKey] = useState('')
  const [markets, setMarkets] = useState([])
  const [marketOptions, setOptions] = useState([])
  const [selectedMarkets, setSelectedMarkets] = useState([])
  const [runModelWithSplit, setRunModelWithSplit] = useState(false)
  const [runSmartSupplyAfter, setRunSmartSupplyAfter] = useState(false)
  const [runDemandModel, setRunDemandModel] = useState(false)
  const [status, setStatus] = useState('')
  const [solverState, setSolverState] = useState()
  const [startRunDate, setStartRunDate] = useState()
  const [lastRunDate, setLastRunDate] = useState()
  const [historyId, setHistoryId] = useState()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [marketSearchValue, setMarketSearchValue] = useState('')
  const [duration, setDuration] = useState()
  const [start, setStart] = useState()
  const { data: metadata } = useMetadata('solver')
  const gridAPIRef = useRef()
  const { updateSetting, getSetting } = useUserSettings()

  const { config, setConfig } = useLpConfig()

  const parseStart = () => {
    if (metadata && start) {
      return metadata.start_options[parseInt(start) - 1]
    }
  }
  const triggerRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['modelHistoryOverviewQuery'],
    })
  }

  const getDemandModelSetting = async () => {
    const setting = await getSetting('optimizer::run-demand-model')
    setRunDemandModel(setting)
  }

  useEffect(() => {
    let isMounted = true
    const getGoogleSheetKey = () => {
      api.getGoogleSheetKey().then((resp) => {
        isMounted && setGoogleSheetKey(resp.data)
      })
    }
    getGoogleSheetKey()
    getDemandModelSetting()
    return () => (isMounted = false)
  }, [])

  useEffect(() => {
    api.getStoreMarkets().then((resp) => {
      setMarkets(resp.data.sort((a, b) => (a.market > b.market ? 1 : -1)))
      setOptions(resp.data.sort((a, b) => (a.market > b.market ? 1 : -1)))
    })
  }, [selectedMarkets])

  useEffect(() => {
    getModalRunStatusAndDetails()
    gridAPIRef?.current?.refreshCells({ force: true })
  }, [status, historyId])

  useInterval(() => {
    getModalRunStatusAndDetails()
    gridAPIRef?.current?.refreshCells({ force: true })
  }, 5000)

  const getModalRunStatusAndDetails = () => {
    api.getSolverState(historyId).then((resp) => {
      const { data } = resp
      setLastRunDate(formatters.DATE_TIME(moment(data.last_run)))
      setStatus(data.last_status || data.latest_runs[0].status)
      setSolverState(data)
      if (data.running_market_names.length !== solverState?.running_market_names.length) {
        triggerRefresh()
      }
      setSolverState(data)
    })
  }

  const runNewModel = () => {
    setStatus('Running')
    setMarketSearchValue('')
    setStartRunDate(start)
    const s = parseStart()
    const parameters = {
      push_storage: false,
      markets: selectedMarkets.map((sm) => sm.market),
      split: runModelWithSplit,
      smart_supply_after: runSmartSupplyAfter,
      start: s,
      duration,
      period_count: 1,
      model_type: runDemandModel ? 'demand' : 'supply',
    }

    api.runModel(parameters).then(() => {
      setSelectedMarkets([])
      triggerRefresh()
    })
  }

  if (googleSheetKey === '' || markets.length === 0 || status === '' || !solverState) return null

  return (
    <div className='model-run-container p-3'>
      <Row className='flex h-100 pb-4'>
        <Configuration
          sheetKey={googleSheetKey}
          status={status}
          startRunDateTime={startRunDate}
          lastRunDateTime={lastRunDate}
          options={marketOptions}
          markets={markets}
          setOptions={setOptions}
          selectedMarkets={selectedMarkets}
          setSelectedMarkets={setSelectedMarkets}
          showModal={() => setIsModalVisible(true)}
          onSplitSwitchClick={(checked) => setRunModelWithSplit(checked)}
          onSmartSupplySwitchClick={(checked) => setRunSmartSupplyAfter(checked)}
          onDemandModelSwitchClick={(checked) => {
            setRunDemandModel(checked)
            updateSetting('optimizer::run-demand-model', checked)
          }}
          runNewModel={runNewModel}
          solverState={solverState}
          marketSearchValue={marketSearchValue}
          setMarketSearchValue={setMarketSearchValue}
          duration={duration}
          setDuration={setDuration}
          start={start}
          setStart={setStart}
          metadata={metadata}
          runSmartSupplyAfter={runSmartSupplyAfter}
          runModelWithSplit={runModelWithSplit}
          runDemandModel={runDemandModel}
        />
        <Col className='mt-3 vertical-flex' style={{ height: 'auto' }} span={18}>
          <div className='flex-1'>
            <span className='section-title mt-1 pl-3'>LOGS</span>
            <Logs logs={solverState?.log_data} />
          </div>
          <div className='mt-3 pl-3 flex-1'>
            <Grid setHistoryId={setHistoryId} gridAPIRef={gridAPIRef} />
          </div>
        </Col>
      </Row>
      {config && (
        <LPSettingsModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          config={config}
          setConfig={setConfig}
        />
      )}
    </div>
  )
}
